import { openDB } from 'idb';
import { IAttainsWatershed } from '../types/attains';

// ATTAINS data is assessment-cycle based (biennial), so a long TTL is fine.
const DB_NAME = 'ceres-attains-cache';
const DB_VERSION = 3;
const STORE_WATERSHED = 'watershed';
const STORE_CROSSWALK = 'dmr-crosswalk';
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
// EPA refreshes the DMR crosswalk infrequently; cache it for a quarter.
const CROSSWALK_TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 days
const CROSSWALK_KEY = 'crosswalk';

interface WatershedEntry {
  huc12: string;
  data: IAttainsWatershed;
  cachedAt: number;
}

interface CrosswalkEntry {
  id: string;
  map: Record<string, string[]>;
  cachedAt: number;
}

function openCacheDB() {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion) {
      // v3 enriched the watershed shape (per-use, probable sources, TMDL, IR docs).
      // Drop pre-enrichment entries so clients refetch the new shape.
      if (oldVersion < 3 && db.objectStoreNames.contains(STORE_WATERSHED)) {
        db.deleteObjectStore(STORE_WATERSHED);
      }
      if (!db.objectStoreNames.contains(STORE_WATERSHED)) {
        db.createObjectStore(STORE_WATERSHED, { keyPath: 'huc12' });
      }
      if (!db.objectStoreNames.contains(STORE_CROSSWALK)) {
        db.createObjectStore(STORE_CROSSWALK, { keyPath: 'id' });
      }
    },
  });
}

function isExpired(cachedAt: number, ttl = TTL_MS): boolean {
  return Date.now() - cachedAt > ttl;
}

export async function getCachedCrosswalk(): Promise<Record<string, string[]> | null> {
  try {
    const db = await openCacheDB();
    const entry: CrosswalkEntry | undefined = await db.get(STORE_CROSSWALK, CROSSWALK_KEY);
    if (!entry || isExpired(entry.cachedAt, CROSSWALK_TTL_MS)) return null;
    return entry.map;
  } catch {
    return null;
  }
}

export async function setCachedCrosswalk(map: Record<string, string[]>): Promise<void> {
  try {
    const db = await openCacheDB();
    const entry: CrosswalkEntry = { id: CROSSWALK_KEY, map, cachedAt: Date.now() };
    await db.put(STORE_CROSSWALK, entry);
  } catch {
    // cache write failure is non-fatal
  }
}

export async function getCachedWatershed(huc12: string): Promise<IAttainsWatershed | null> {
  try {
    const db = await openCacheDB();
    const entry: WatershedEntry | undefined = await db.get(STORE_WATERSHED, huc12);
    if (!entry || isExpired(entry.cachedAt)) return null;
    return entry.data;
  } catch {
    return null;
  }
}

export async function setCachedWatershed(huc12: string, data: IAttainsWatershed): Promise<void> {
  try {
    const db = await openCacheDB();
    const entry: WatershedEntry = { huc12, data, cachedAt: Date.now() };
    await db.put(STORE_WATERSHED, entry);
  } catch {
    // cache write failure is non-fatal
  }
}
